/**
 * ScheduleEditor — the humane "cron without cron" editor from
 * design/project/lib/dash2.jsx, bound to the real schedules API.
 * Bottom sheet on phone / centered modal ≥900px (the kit Sheet decides).
 *
 * Differences from the prototype, all factual:
 * - round-trips REAL 6-field EventBridge cron (min hr dom mon dow yr,
 *   no cron() wrapper — contract phase 2 §2), not 5-field crontab;
 * - carries the phase-6 §4 delivery mode (message/goal/loop) and the
 *   optional loop interval;
 * - keeps prompt + timezone from the existing create form;
 * - unparseable crons (ranges, steps, …) stay editable as raw text —
 *   the humane controls take over once the expression fits the model.
 */
import { useMemo, useState } from "react";
import {
  PlanLimitError,
  createSchedule,
  deleteSchedule,
  isValidModel,
  updateSchedule,
  type Agent,
  type Effort,
  type RunMode,
  type Schedule,
} from "../lib/api";
import { Sheet, Stepper, Btn, Toggle, Icon, I } from "../kit";
import { ModelEffortFields } from "./ModelEffortFields";
import { PlanLimitNote } from "./PlanLimitNote";

const DEFAULT_TZ = "America/New_York";

export type Repeat = "daily" | "weekdays" | "weekends" | "custom";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
/** EventBridge day-of-week names, index-aligned with DAY_LABELS. */
const DAY_NAMES = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
/** EventBridge numeric day-of-week (1 = Sunday … 7 = Saturday). */
const DAY_NUMS = [2, 3, 4, 5, 6, 7, 1] as const;

export interface CronModel {
  min: number;
  hour: number;
  repeat: Repeat;
  /** Mon..Sun selection for repeat === "custom". */
  custom: boolean[];
}

const DEFAULT_MODEL: CronModel = {
  min: 0,
  hour: 2,
  repeat: "daily",
  custom: [true, true, true, true, true, false, false],
};

/** Build a 6-field EventBridge expression from the humane model. */
export function buildCron(m: CronModel): string {
  let dom = "*";
  let dow = "?";
  if (m.repeat === "weekdays") {
    dom = "?";
    dow = "MON-FRI";
  } else if (m.repeat === "weekends") {
    dom = "?";
    dow = "SAT,SUN";
  } else if (m.repeat === "custom") {
    const sel = DAY_NAMES.filter((_, i) => m.custom[i]);
    if (sel.length > 0 && sel.length < 7) {
      dom = "?";
      dow = sel.join(",");
    }
  }
  return `${m.min} ${m.hour} ${dom} * ${dow} *`;
}

/** One day-of-week token (name or EventBridge 1-7 number) → Mon..Sun index. */
function dayIndex(token: string): number | null {
  const t = token.trim().toUpperCase();
  const byName = DAY_NAMES.findIndex((n) => n === t || t.startsWith(n));
  if (byName >= 0) return byName;
  const n = Number(t);
  if (Number.isInteger(n)) {
    const byNum = DAY_NUMS.findIndex((d) => d === n);
    if (byNum >= 0) return byNum;
  }
  return null;
}

/**
 * Parse a 5- or 6-field cron back into the humane model — the round-trip.
 * Returns null when the expression doesn't fit (steps, ranges beyond
 * day-of-week, multiple times, …); the editor then keeps it as raw text.
 */
export function parseCron(str: string): CronModel | null {
  const p = str.trim().split(/\s+/);
  if (p.length !== 5 && p.length !== 6) return null;
  const [minS, hourS, dom, mon, dow] = p;
  const min = Number(minS);
  const hour = Number(hourS);
  if (!Number.isInteger(min) || min < 0 || min > 59) return null;
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
  if (dom !== "*" && dom !== "?") return null;
  if (mon !== "*") return null;

  const d = dow.trim().toUpperCase();
  if (d === "*" || d === "?") {
    return { ...DEFAULT_MODEL, min, hour, repeat: "daily" };
  }
  if (d === "MON-FRI" || d === "2-6") {
    return { ...DEFAULT_MODEL, min, hour, repeat: "weekdays" };
  }
  if (d === "SAT,SUN" || d === "SUN,SAT" || d === "7,1" || d === "1,7") {
    return { ...DEFAULT_MODEL, min, hour, repeat: "weekends" };
  }
  // Comma list (or a single range) of day names/numbers → custom pills.
  const custom = [false, false, false, false, false, false, false];
  for (const token of d.split(",")) {
    const range = token.split("-");
    if (range.length === 2) {
      const a = dayIndex(range[0]);
      const b = dayIndex(range[1]);
      if (a === null || b === null) return null;
      // Walk the EventBridge week ring from a to b inclusive.
      let i = a;
      for (let guard = 0; guard < 8; guard++) {
        custom[i] = true;
        if (i === b) break;
        i = (i + 1) % 7;
      }
    } else {
      const i = dayIndex(token);
      if (i === null) return null;
      custom[i] = true;
    }
  }
  if (!custom.some(Boolean)) return null;
  return { min, hour, repeat: "custom", custom };
}

/** "Every day at 2:00 AM" / "Weekdays at …" — the humane readback. */
export function humanizeModel(m: CronModel): string {
  const h12 = ((m.hour + 11) % 12) + 1;
  const ap = m.hour < 12 ? "AM" : "PM";
  const time = `${h12}:${String(m.min).padStart(2, "0")} ${ap}`;
  let when = "Every day";
  if (m.repeat === "weekdays") when = "Weekdays";
  else if (m.repeat === "weekends") when = "Weekends";
  else if (m.repeat === "custom") {
    const sel = DAY_LABELS.filter((_, i) => m.custom[i]);
    when =
      sel.length === 0
        ? "No days selected"
        : sel.length === 7
          ? "Every day"
          : sel.join(", ");
  }
  return `${when} at ${time}`;
}

/** Humane description of a stored cron, or null when it doesn't parse. */
export function humanizeCron(cron: string): string | null {
  const m = parseCron(cron);
  return m ? humanizeModel(m) : null;
}

/** "HH:MM" time-of-day of a stored cron, for run-order rows. */
export function cronTime(cron: string): string | null {
  const m = parseCron(cron);
  if (!m) return null;
  return `${String(m.hour).padStart(2, "0")}:${String(m.min).padStart(2, "0")}`;
}

const REPEATS: [Repeat, string][] = [
  ["daily", "Nightly"],
  ["weekdays", "Weekdays"],
  ["weekends", "Weekends"],
  ["custom", "Custom"],
];

const MODES: [RunMode, string][] = [
  ["message", "Message"],
  ["goal", "Goal"],
  ["loop", "Loop"],
];

export function ScheduleEditor({
  seed,
  agents,
  onClose,
  onSaved,
}: {
  /** Existing schedule to edit, or null to create. */
  seed: Schedule | null;
  agents: Agent[];
  onClose: () => void;
  onSaved: (note: string) => void;
}) {
  const seedModel = useMemo(
    () => (seed ? parseCron(seed.cron) : null),
    [seed]
  );

  const [name, setName] = useState(seed?.name ?? "");
  const [agentName, setAgentName] = useState(
    seed?.agentName ?? agents[0]?.agentName ?? ""
  );
  const [prompt, setPrompt] = useState(seed?.prompt ?? "");
  const [timezone, setTimezone] = useState(seed?.timezone ?? DEFAULT_TZ);
  const [mode, setMode] = useState<RunMode>(seed?.mode ?? "message");
  const [loopInterval, setLoopInterval] = useState(seed?.loopInterval ?? "");
  // Phase 9 §C per-schedule override ("" = inherit: agent default, then
  // Claude Code's own) + §B2 alwaysRun (exempt from budget holds).
  const [runModel, setRunModel] = useState(seed?.model ?? "");
  const [runEffort, setRunEffort] = useState<"" | Effort>(seed?.effort ?? "");
  const [alwaysRun, setAlwaysRun] = useState(seed?.alwaysRun ?? false);
  // Session-autoClose per-schedule override. Schedules CLOSE by default
  // (start fresh each run), so the toggle seeds ON when unset — WYSIWYG:
  // ON = close, OFF = keep warm. (Seeding false would have shown "off" for
  // a schedule that actually auto-closes by default — a footgun.) The
  // explicit boolean rides every save (both paths) so what the toggle shows
  // is what's persisted.
  const [autoClose, setAutoClose] = useState(seed?.autoClose ?? true);
  const [model, setModel] = useState<CronModel>(seedModel ?? DEFAULT_MODEL);
  // Raw mode: the stored/typed cron doesn't fit the humane model — the
  // text is authoritative until an edit makes it parseable again.
  const [rawCron, setRawCron] = useState<string | null>(
    seed && !seedModel ? seed.cron : null
  );
  const [showCron, setShowCron] = useState(seed !== null && !seedModel);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Phase 10 §C: POST /schedules answers 402 plan_limit past the free
  // tier's schedule count, and goal/loop modes 402 without Pro — render
  // the inline upgrade note instead of the raw error.
  const [planErr, setPlanErr] = useState<PlanLimitError | null>(null);

  const cron = rawCron ?? buildCron(model);
  const human = rawCron === null ? humanizeModel(model) : null;
  const noDays =
    rawCron === null &&
    model.repeat === "custom" &&
    !model.custom.some(Boolean);

  /** Humane control edits always exit raw mode. */
  const update = (patch: Partial<CronModel>) => {
    setModel((m) => ({ ...m, ...patch }));
    setRawCron(null);
  };

  const onCronText = (text: string) => {
    const parsed = parseCron(text);
    if (parsed) {
      setModel(parsed);
      setRawCron(null);
    } else {
      setRawCron(text);
    }
  };

  const save = async () => {
    if (busy) return;
    if (!name.trim() || !agentName || !prompt.trim()) {
      setErr("task, agent, and prompt are required");
      return;
    }
    if (cron.trim().split(/\s+/).length !== 6) {
      setErr("cron needs 6 fields (EventBridge format, no cron() wrapper)");
      return;
    }
    if (noDays) {
      setErr("pick at least one day");
      return;
    }
    if (runModel && !isValidModel(runModel)) {
      setErr(
        "model must be opus/sonnet/haiku or a raw id (a-z, 0-9, dot, hyphen)"
      );
      return;
    }
    const interval = loopInterval.trim();
    // PUT /schedules/{id} merges fields: absent = keep stored, and the plane
    // rejects an empty loopInterval (no clear semantics yet). Refuse the save
    // rather than silently keeping the old interval behind a "saved" toast.
    if (seed && mode === "loop" && seed.loopInterval && !interval) {
      setErr(
        "the plane can't clear a stored loop interval yet — enter one, or recreate the schedule"
      );
      return;
    }
    // Phase 9 §C: same field-merge limitation — "inherit" is the ABSENCE of
    // the field, which a merge-PUT can't express. Refuse a clear honestly.
    if (seed && seed.model && !runModel) {
      setErr(
        "the plane can't clear a stored model override yet — pick a model, or recreate the schedule"
      );
      return;
    }
    if (seed && seed.effort && !runEffort) {
      setErr(
        "the plane can't clear a stored effort override yet — pick an effort, or recreate the schedule"
      );
      return;
    }
    setBusy(true);
    setErr(null);
    setPlanErr(null);
    const base = {
      name: name.trim(),
      cron: cron.trim(),
      agentName,
      prompt: prompt.trim(),
      timezone: timezone.trim() || DEFAULT_TZ,
    };
    try {
      if (seed) {
        // Edit is a field-merge on the plane (the PUT handler builds its
        // UpdateExpression only from provided fields), so omitting mode here
        // would silently KEEP a stored goal/loop mode — always send it,
        // including the explicit "message" default. loopInterval rides along
        // whenever the schedule stays a loop; a stale stored interval on a
        // goal/message schedule is runtime-harmless (the daemon only reads
        // it in loop mode) and resurfaces honestly if loop is re-selected.
        await updateSchedule(seed.scheduleId, {
          ...base,
          mode,
          ...(mode === "loop" && interval ? { loopInterval: interval } : {}),
          // Phase 9 §C/§B2: set values ride; clears are blocked above.
          // alwaysRun is a boolean, so it's always expressible — send it
          // every save (like mode) so a merge-PUT can flip it off.
          ...(runModel ? { model: runModel } : {}),
          ...(runEffort ? { effort: runEffort } : {}),
          alwaysRun,
          // Session-autoClose: boolean, always expressible — send it every
          // save so a merge-PUT can flip the override on or off.
          autoClose,
        });
        onSaved("Schedule saved");
      } else {
        // Create: POST treats absent mode as "message" (phase 6 §4) — keep
        // omitting the defaults so the request shape stays identical to
        // phase 2 (backward compat with older planes).
        await createSchedule({
          ...base,
          enabled: true,
          ...(mode !== "message" ? { mode } : {}),
          ...(mode === "loop" && interval ? { loopInterval: interval } : {}),
          // Phase 9 §C/§B2 — omitted at defaults (inherit / budget-governed).
          ...(runModel ? { model: runModel } : {}),
          ...(runEffort ? { effort: runEffort } : {}),
          ...(alwaysRun ? { alwaysRun: true } : {}),
          // Session-autoClose: the toggle is WYSIWYG (seeded to the schedule
          // default = close), so send the explicit boolean — ON persists
          // close, OFF persists keep-warm — consistent with the edit path.
          autoClose,
        });
        onSaved("Schedule created");
      }
    } catch (e) {
      if (e instanceof PlanLimitError) {
        setPlanErr(e); // inline upgrade note, no modal
      } else {
        setErr(e instanceof Error ? e.message : "save failed");
      }
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!seed || deleting) return;
    if (!window.confirm(`Delete schedule "${seed.name}"?`)) return;
    setDeleting(true);
    setErr(null);
    try {
      await deleteSchedule(seed.scheduleId);
      onSaved("Schedule deleted");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "delete failed");
      setDeleting(false);
    }
  };

  return (
    <Sheet title={seed ? "Edit schedule" : "New schedule"} onClose={onClose}>
      <div className="dsh-form">
        <div>
          <span className="dsh-field-label">Task</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Draft the morning brief"
          />
        </div>

        <div>
          <span className="dsh-field-label">Agent</span>
          {agents.length === 0 ? (
            <p className="hint">
              No agents yet — create one in the Swarm tab first; schedules route their
              prompt to an agent.
            </p>
          ) : (
            <div className="dsh-pills">
              {agents.map((a) => (
                <button
                  key={a.agentName}
                  type="button"
                  className={`dsh-pill${agentName === a.agentName ? " on" : ""}`}
                  onClick={() => setAgentName(a.agentName)}
                >
                  {a.agentName}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="dsh-field-label">Repeat</span>
          <div className="dsh-seg">
            {REPEATS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={
                  rawCron === null && model.repeat === k ? "on" : undefined
                }
                onClick={() => update({ repeat: k })}
              >
                {label}
              </button>
            ))}
          </div>
          {rawCron === null && model.repeat === "custom" && (
            <div className="dsh-days">
              {DAY_LABELS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  className={model.custom[i] ? "on" : undefined}
                  aria-label={d}
                  onClick={() =>
                    update({
                      custom: model.custom.map((v, j) => (j === i ? !v : v)),
                    })
                  }
                >
                  {d[0]}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="dsh-field-label">Time (runs overnight)</span>
          <div className="dsh-time">
            <Stepper
              label="hour"
              value={model.hour}
              fmt={(v) => String(v).padStart(2, "0")}
              onDec={() => update({ hour: (model.hour + 23) % 24 })}
              onInc={() => update({ hour: (model.hour + 1) % 24 })}
            />
            <span className="colon">:</span>
            <Stepper
              label="minute"
              value={model.min}
              fmt={(v) => String(v).padStart(2, "0")}
              onDec={() => update({ min: (model.min + 55) % 60 })}
              onInc={() => update({ min: (model.min + 5) % 60 })}
            />
            <span className="ampm">{model.hour < 12 ? "AM" : "PM"}</span>
          </div>
        </div>

        <div>
          <span className="dsh-field-label">Mode</span>
          <div className="dsh-seg cols-3">
            {MODES.map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={mode === k ? "on" : undefined}
                onClick={() => setMode(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="hint" style={{ marginTop: 8 }}>
            goal: the agent works the objective autonomously · loop: repeats
            on an interval
          </p>
          {mode === "loop" && (
            <div style={{ marginTop: 10 }}>
              <span className="dsh-field-label">Loop interval (optional)</span>
              <input
                className="input mono"
                placeholder="10m — empty lets Claude Code self-pace"
                value={loopInterval}
                onChange={(e) => setLoopInterval(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* phase 9 §C: per-schedule override — beats the agent default */}
        <ModelEffortFields
          model={runModel}
          effort={runEffort}
          onModel={setRunModel}
          onEffort={setRunEffort}
          hint={`override for this schedule's runs — "inherit" uses the agent's default, then Claude Code's own`}
        />

        {/* phase 9 §B2: exempt from budget holds */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Toggle
            on={alwaysRun}
            onClick={() => setAlwaysRun((v) => !v)}
            aria-label={
              alwaysRun ? "disable always run" : "enable always run"
            }
          />
          <span style={{ fontSize: 13, color: "var(--color-fg-2)" }}>
            always run — exempt from budget holds
            <span className="hint" style={{ display: "block", fontSize: 12 }}>
              fires even when the nightly token cap is reached (e.g. email
              triage every 2h) — a hard seat limit still holds everything
            </span>
          </span>
        </div>

        {/* session-autoClose: per-schedule override of the close-on-complete
            behavior. Scheduled fires default to CLOSE (fresh each run). */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Toggle
            on={autoClose}
            onClick={() => setAutoClose((v) => !v)}
            aria-label={
              autoClose ? "disable auto-close" : "enable auto-close"
            }
          />
          <span style={{ fontSize: 13, color: "var(--color-fg-2)" }}>
            Close session when the task finishes (start fresh next run)
            <span className="hint" style={{ display: "block", fontSize: 12 }}>
              scheduled runs close by default — leave this off to inherit; turn
              it on to force a fresh session even if the agent default keeps
              warm
            </span>
          </span>
        </div>

        <div>
          <span className="dsh-field-label">Prompt</span>
          <textarea
            className="input"
            rows={3}
            placeholder="What should the agent do each run?"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        {/* live summary + cron round-trip */}
        <div className="dsh-summary">
          <div className="dsh-summary-human">
            <Icon d={I.calendar} size={15} />
            <span>{human ?? "Custom cron expression"}</span>
          </div>
          <div className="dsh-summary-next">
            {noDays
              ? "Next run: never (no days selected)"
              : rawCron === null
                ? `Next run: tonight · ${String(model.hour).padStart(2, "0")}:${String(model.min).padStart(2, "0")} (${timezone.trim() || DEFAULT_TZ})`
                : `Runs on the raw expression below (${timezone.trim() || DEFAULT_TZ})`}
          </div>
          <div className="dsh-summary-cron">
            <span className="k">cron</span>
            <code>{cron}</code>
            <button
              type="button"
              className="edit"
              onClick={() => setShowCron((s) => !s)}
            >
              {showCron ? "hide" : "edit cron"}
            </button>
          </div>
          {showCron && (
            <input
              className="input dsh-cron-input"
              value={cron}
              onChange={(e) => onCronText(e.target.value)}
              aria-label="cron expression"
              spellCheck={false}
            />
          )}
          {showCron && (
            <p className="hint" style={{ marginTop: 8 }}>
              6 fields: min hr day month weekday year (EventBridge, no cron()
              wrapper)
            </p>
          )}
        </div>

        <div>
          <span className="dsh-field-label">Timezone</span>
          <input
            className="input mono"
            placeholder={DEFAULT_TZ}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          />
        </div>

        {err && <span className="dsh-hint-error">{err}</span>}
        {planErr && <PlanLimitNote err={planErr} />}

        <div className="dsh-form-actions">
          {seed && (
            <Btn variant="danger" onClick={remove} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </Btn>
          )}
          <Btn variant="outline" full onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            full
            onClick={save}
            disabled={busy || !name.trim() || !agentName || !prompt.trim()}
          >
            {busy ? "Saving…" : seed ? "Save changes" : "Create schedule"}
          </Btn>
        </div>
      </div>
    </Sheet>
  );
}
